import { useState } from "react";
import t from "@/lang/lang";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { TextInput } from "@/shared/components/form/text-input";
import { BooleanSelect } from "@/shared/components/form/boolean-select";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { LabApiKeySelect } from "@/services/backend/samples/components/lab-api-key-select";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useSamplesLaboratoryPutMutation } from "@/services/backend/samples/api/lab-api";
import { Laboratory } from "@/services/backend/samples/types/laboratory";
import { AddressCard } from "../../addresses/components/address-card";

export function EditLabCard({ lab }: { lab: Laboratory }) {
  const [request, setRequest] = useState<Laboratory>(lab);
  const mutation = useSamplesLaboratoryPutMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, lab);

  return (
    <div className="grid gap-2">
      <BaseData lab={request} onLabChange={setRequest} error={error} />
      <AddressCard
        address={request.address}
        onAddressChange={(address) => setRequest({ ...request, address })}
      />
    </div>
  );
}

function BaseData({
  lab,
  onLabChange,
  error,
}: {
  lab: Laboratory;
  onLabChange: (lab: Laboratory) => void;
  error: unknown;
}) {
  return (
    <Card>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <TextInput
          label={t("Name des Labors")}
          field="name"
          text={lab.name}
          onTextChange={(name) => onLabChange({ ...lab, name })}
          errors={inputValidationErrorMessages(error, "name")}
        />
        <div className="grid gap-1.5">
          <Label htmlFor="api">{t("API-Anbindung")}</Label>
          <LabApiKeySelect
            value={lab.api}
            onValueChange={(api) => onLabChange({ ...lab, api })}
          />
          <InputValidationErrors field="api" error={error} />
        </div>
        <BooleanSelect
          label={t("Analysiert Luftkeimproben")}
          field="analysesAirMicrobialSamples"
          value={lab.analysesAirMicrobialSamples}
          onValueChange={(analysesAirMicrobialSamples) =>
            onLabChange({ ...lab, analysesAirMicrobialSamples })
          }
          errors={inputValidationErrorMessages(
            error,
            "analysesAirMicrobialSamples",
          )}
        />
        <BooleanSelect
          label={t("Analysiert Staubdichteproben")}
          field="analysesDustDensitySamples"
          value={lab.analysesDustDensitySamples}
          onValueChange={(analysesDustDensitySamples) =>
            onLabChange({ ...lab, analysesDustDensitySamples })
          }
          errors={inputValidationErrorMessages(
            error,
            "analysesDustDensitySamples",
          )}
        />
        <BooleanSelect
          label={t("Analysiert Oberflächenkeimproben")}
          field="analysesSurfaceMicrobialSamples"
          value={lab.analysesSurfaceMicrobialSamples}
          onValueChange={(analysesSurfaceMicrobialSamples) =>
            onLabChange({ ...lab, analysesSurfaceMicrobialSamples })
          }
          errors={inputValidationErrorMessages(
            error,
            "analysesSurfaceMicrobialSamples",
          )}
        />
        <BooleanSelect
          label={t("Analysiert Wasserproben")}
          field="analysesWaterSamples"
          value={lab.analysesWaterSamples}
          onValueChange={(analysesWaterSamples) =>
            onLabChange({ ...lab, analysesWaterSamples })
          }
          errors={inputValidationErrorMessages(error, "analysesWaterSamples")}
        />
      </CardContent>
    </Card>
  );
}
